import { rtkUsersClient } from '@gm-commercial/rtk-clients';
import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useUser } from '~/common/contexts/UserContext';
import {
  findMatchingRouteFromPathname,
  setAppSpaceViewed,
} from '~/common/utils/route/route.utils';
import type { TrackableAppSpaceCode } from '~/features/welcomeExperience/gettingStarted.model';

/**
 * Marks a welcome-experience app space as viewed when the current route maps
 * to a trackable space the signed-in user has not already seen.
 */
export const AppSpaceUpdater = (): null => {
  const { pathname } = useLocation();
  const { profile, refetch: refetchUser } = useUser();
  const [markAppSpaceViewed] = rtkUsersClient.useMarkAppSpaceViewedMutation();

  const handleSetAppSpaceViewed = useCallback(
    (spaceCode: TrackableAppSpaceCode) => {
      if (!profile?.id || profile.viewedAppSpaceCodes?.includes(spaceCode)) {
        return;
      }

      markAppSpaceViewed({
        profileId: profile.id,
        space: spaceCode,
      })
        .unwrap()
        .then(() => {
          refetchUser();
        })
        .catch(() => {
          console.error('Failed to mark app space viewed: ', spaceCode);
        });
    },
    [profile, refetchUser, markAppSpaceViewed]
  );

  useEffect(() => {
    if (!profile) {
      return;
    }
    const currentRoute = findMatchingRouteFromPathname(pathname);
    if (currentRoute?.name) {
      setAppSpaceViewed(currentRoute.name, handleSetAppSpaceViewed);
    }
  }, [profile, pathname, handleSetAppSpaceViewed]);

  return null;
};
